// components/RecordingStudio/Timeline.jsx
import React, { useRef, useCallback, useState, useEffect } from "react";
import { Scissors } from "lucide-react";

const BASE_PIXELS_PER_SECOND = 80;
const TRACK_HEIGHT = 72;
const RULER_HEIGHT = 36;
const LABEL_WIDTH = 148;
const SNAP_INTERVAL = 0.25;
const MIN_DURATION_SEC = 0.25;

const BLOCK_COLORS = [
  {
    grad: ["#7c3aed", "#6d28d9"],
    glow: "rgba(124,58,237,0.35)",
    border: "rgba(139,92,246,0.5)",
  },
  {
    grad: ["#0891b2", "#0e7490"],
    glow: "rgba(8,145,178,0.35)",
    border: "rgba(6,182,212,0.5)",
  },
  {
    grad: ["#059669", "#047857"],
    glow: "rgba(5,150,105,0.35)",
    border: "rgba(16,185,129,0.5)",
  },
  {
    grad: ["#dc2626", "#b91c1c"],
    glow: "rgba(220,38,38,0.35)",
    border: "rgba(248,113,113,0.5)",
  },
  {
    grad: ["#d97706", "#b45309"],
    glow: "rgba(217,119,6,0.35)",
    border: "rgba(251,191,36,0.5)",
  },
  {
    grad: ["#7c3aed", "#4f46e5"],
    glow: "rgba(79,70,229,0.35)",
    border: "rgba(129,140,248,0.5)",
  },
];

const snap = (v) => Math.round(v / SNAP_INTERVAL) * SNAP_INTERVAL;

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ds = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, "0")}.${ds}`;
};
const fmtRuler = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const getDurSec = (block) => {
  if (!block.duration || block.duration <= 0) return null;
  return block.duration > 300 ? block.duration / 1000 : block.duration;
};

const getMusicDisplayDuration = (backgroundMusic) => {
  if (!backgroundMusic) return null;
  const trimStart = Number(backgroundMusic.trimStart || 0);
  const trimEndRaw = backgroundMusic.trimEnd;
  if (
    trimEndRaw !== null &&
    trimEndRaw !== undefined &&
    Number(trimEndRaw) > trimStart
  ) {
    return Number(trimEndRaw) - trimStart;
  }
  return backgroundMusic.duration ?? null;
};

const MiniWave = ({ count = 10, animated = false, color = "white" }) => (
  <div className="flex items-center gap-[2px]" style={{ opacity: 0.5 }}>
    {Array.from({ length: count }).map((_, i) => {
      const h = [5, 12, 8, 15, 6, 13, 9, 16, 7, 11][i % 10];
      return (
        <div
          key={i}
          style={{
            width: 2,
            height: animated ? `${h}px` : "3px",
            background: color,
            borderRadius: 9999,
            animationDelay: animated ? `${i * 60}ms` : undefined,
            transition: "height 0.2s ease",
          }}
          className={animated ? "animate-pulse" : ""}
        />
      );
    })}
  </div>
);

const Timeline = ({
  blocks = [],
  backgroundMusic = null,
  currentTime = 0,
  isPlaying = false,
  onSeek,
  onBlockMove,
  onBlockResize,
  onMusicResize,
  onTrimClick,
  onBlockSelect,
  selectedBlockId,
}) => {
  const scrollRef = useRef(null);
  const dragStateRef = useRef(null);
  const [ghostPos, setGhostPos] = useState(null);
  const [musicGhostDur, setMusicGhostDur] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [scrollAreaWidth, setScrollAreaWidth] = useState(800);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Observe scrollable area width for responsive scaling
  useEffect(() => {
    if (!scrollRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setScrollAreaWidth(entry.contentRect.width);
    });
    ro.observe(scrollRef.current);
    return () => ro.disconnect();
  }, []);

  const totalDuration = Math.max(
    blocks.reduce((acc, b) => {
      const start = parseFloat(b.timelineStart) || 0;
      const dur = getDurSec(b) ?? 5;
      return Math.max(acc, start + dur);
    }, 30),
    30,
  );

  // Fit-to-width: compute PPS so entire timeline fills the scroll area
  // Clamp between 8 px/s (very long content) and BASE_PIXELS_PER_SECOND before zoom
  const fitPPS = Math.max(
    8,
    Math.min(BASE_PIXELS_PER_SECOND, (scrollAreaWidth - 32) / totalDuration),
  );
  const PIXELS_PER_SECOND = fitPPS * zoomLevel;
  const MIN_BLOCK_WIDTH = MIN_DURATION_SEC * PIXELS_PER_SECOND;

  const totalWidth = totalDuration * PIXELS_PER_SECOND + 80;

  // Adapt ruler tick interval so labels are never too dense
  // Target ~80px between major ticks minimum
  const rawInterval = Math.ceil(80 / PIXELS_PER_SECOND);
  const majorInterval =
    [1, 2, 5, 10, 15, 30, 60].find((v) => v >= rawInterval) ?? 60;
  const majorTicks = Array.from(
    { length: Math.ceil(totalDuration / majorInterval) + 1 },
    (_, i) => i * majorInterval,
  );
  const minorTicks = Array.from(
    { length: Math.ceil(totalDuration) + 1 },
    (_, i) => i,
  ).filter((t) => t % majorInterval !== 0);

  const ppsRef = useRef(PIXELS_PER_SECOND);
  ppsRef.current = PIXELS_PER_SECOND;

  const handleRulerClick = useCallback(
    (e) => {
      if (dragStateRef.current) return;
      if (!scrollRef.current) return;
      const rect = scrollRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left + scrollRef.current.scrollLeft;
      onSeek?.(Math.max(0, snap(x / ppsRef.current)));
    },
    [onSeek],
  );

  const handleBlockMouseDown = useCallback(
    (e, block, mode = "move") => {
      e.stopPropagation();
      e.preventDefault();
      onBlockSelect?.(block.id);
      const startTimelineStart = parseFloat(block.timelineStart) || 0;
      const startDurationSec = getDurSec(block) ?? 5;
      dragStateRef.current = {
        blockId: block.id,
        mode,
        startClientX: e.clientX,
        startScrollLeft: scrollRef.current?.scrollLeft ?? 0,
        startTimelineStart,
        startDurationSec,
      };
      setGhostPos({
        blockId: block.id,
        timelineStart: startTimelineStart,
        durationSec: startDurationSec,
      });
    },
    [onBlockSelect],
  );

  useEffect(() => {
    const onMouseMove = (e) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const scrollDelta =
        (scrollRef.current?.scrollLeft ?? 0) - ds.startScrollLeft;
      const dx = e.clientX - ds.startClientX + scrollDelta;
      const dtSec = dx / ppsRef.current;
      if (ds.mode === "move") {
        const newStart = snap(Math.max(0, ds.startTimelineStart + dtSec));
        setGhostPos((prev) =>
          prev ? { ...prev, timelineStart: newStart } : prev,
        );
      } else if (ds.mode === "music-resize") {
        const newDur = snap(Math.max(1, ds.startDurationSec + dtSec));
        setMusicGhostDur(newDur);
      } else {
        const newDur = snap(
          Math.max(MIN_DURATION_SEC, ds.startDurationSec + dtSec),
        );
        setGhostPos((prev) => (prev ? { ...prev, durationSec: newDur } : prev));
      }
    };

    const onMouseUp = () => {
      const ds = dragStateRef.current;
      if (!ds) return;
      dragStateRef.current = null;
      if (ds.mode === "music-resize") {
        setMusicGhostDur((dur) => {
          if (dur !== null) onMusicResize?.(dur);
          return null;
        });
      } else {
        setGhostPos((prev) => {
          if (prev) {
            if (ds.mode === "move")
              onBlockMove?.(ds.blockId, prev.timelineStart);
            else
              onBlockResize?.(ds.blockId, Math.round(prev.durationSec * 1000));
          }
          return null;
        });
      }
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onBlockMove, onBlockResize, onMusicResize]);

  useEffect(() => {
    if (!isPlaying || !scrollRef.current) return;
    const px = currentTime * ppsRef.current;
    const { scrollLeft, clientWidth } = scrollRef.current;
    if (px > scrollLeft + clientWidth * 0.75) {
      scrollRef.current.scrollLeft = px - clientWidth * 0.2;
    }
  }, [currentTime, isPlaying]);

  const playheadX = currentTime * PIXELS_PER_SECOND;
  const isDragging = !!ghostPos || musicGhostDur !== null;

  return (
    <div
      className="flex flex-col h-full rounded-2xl overflow-hidden"
      style={{
        background: "rgba(0,0,0,0.4)",
        border: "1px solid rgba(255,255,255,0.06)",
        userSelect: "none",
      }}
    >
      <div className="flex flex-1 overflow-hidden">
        {/* ── Label column ── */}
        <div
          className="flex-shrink-0"
          style={{
            width: LABEL_WIDTH,
            borderRight: "1px solid rgba(255,255,255,0.05)",
            background: "rgba(0,0,0,0.3)",
          }}
        >
          {/* Ruler corner */}
          <div
            style={{
              height: RULER_HEIGHT,
              borderBottom: "1px solid rgba(255,255,255,0.05)",
            }}
            className="flex items-end px-3 pb-2"
          >
            <span
              className="text-[9px] font-bold uppercase tracking-[0.15em]"
              style={{ color: "rgba(255,255,255,0.2)" }}
            >
              Tracks
            </span>
          </div>

          {/* Voice block labels */}
          {blocks.length === 0 ? (
            <div
              style={{ height: TRACK_HEIGHT }}
              className="flex items-center px-4"
            >
              <span
                className="text-xs"
                style={{ color: "rgba(255,255,255,0.15)" }}
              >
                No blocks
              </span>
            </div>
          ) : (
            blocks.map((block, i) => {
              const clr = BLOCK_COLORS[i % BLOCK_COLORS.length];
              const isSelected = selectedBlockId === block.id;
              return (
                <div
                  key={block.id}
                  style={{
                    height: TRACK_HEIGHT,
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                    background: isSelected ? `${clr.grad[0]}12` : "transparent",
                    cursor: "pointer",
                  }}
                  className="flex items-center px-3 gap-2.5 transition-all"
                  onClick={() => onBlockSelect?.(block.id)}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.03)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {/* Color indicator */}
                  <div
                    style={{
                      width: 3,
                      height: 28,
                      borderRadius: 9999,
                      background: `linear-gradient(180deg, ${clr.grad[0]}, ${clr.grad[1]})`,
                      flexShrink: 0,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-semibold truncate leading-tight"
                      style={{
                        color: isSelected ? "#fff" : "rgba(255,255,255,0.65)",
                      }}
                    >
                      {block.voiceDisplayName || `Block ${i + 1}`}
                    </p>
                  </div>
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      background:
                        block.status === "completed"
                          ? "#34d399"
                          : block.status === "processing"
                            ? "#fbbf24"
                            : "rgba(255,255,255,0.2)",
                    }}
                    className={
                      block.status === "processing" ? "animate-pulse" : ""
                    }
                  />
                </div>
              );
            })
          )}

          {/* Music label */}
          <div
            style={{
              height: TRACK_HEIGHT,
              borderTop: "1px solid rgba(255,255,255,0.05)",
            }}
            className="flex items-center px-3 gap-2.5 group/music"
          >
            <div
              style={{
                width: 3,
                height: 28,
                borderRadius: 9999,
                background: "linear-gradient(180deg, #d97706, #b45309)",
                flexShrink: 0,
              }}
            />
            <div className="flex-1 min-w-0">
              <p
                className="text-xs font-semibold"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                Music
              </p>
              <p
                className="text-[10px] mt-0.5"
                style={{ color: "rgba(255,255,255,0.2)" }}
              >
                Background
              </p>
            </div>
            {onTrimClick && (
              <button
                onClick={onTrimClick}
                className="opacity-0 group-hover/music:opacity-100 transition-opacity p-1 rounded-lg"
                style={{ background: "rgba(217,119,6,0.15)", color: "#fbbf24" }}
              >
                <Scissors size={11} />
              </button>
            )}
          </div>
        </div>

        {/* ── Scrollable area ── */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden relative"
          style={{
            cursor: isDragging ? "grabbing" : "default",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(139,92,246,0.3) transparent",
          }}
        >
          <div
            style={{
              width: totalWidth,
              position: "relative",
              minHeight: "100%",
            }}
          >
            {/* ── Ruler ── */}
            <div
              style={{
                height: RULER_HEIGHT,
                position: "sticky",
                top: 0,
                zIndex: 30,
                background: "rgba(5,7,12,0.95)",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                cursor: "pointer",
              }}
              onClick={handleRulerClick}
            >
              {majorTicks.map((t) => (
                <div
                  key={t}
                  style={{
                    position: "absolute",
                    left: t * PIXELS_PER_SECOND,
                    bottom: 0,
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 5,
                      fontSize: 9,
                      fontFamily: "monospace",
                      color: "rgba(255,255,255,0.3)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtRuler(t)}
                  </span>
                  <div
                    style={{
                      width: 1,
                      height: 10,
                      background: "rgba(255,255,255,0.12)",
                    }}
                  />
                </div>
              ))}
              {minorTicks.map((t) => (
                <div
                  key={`m${t}`}
                  style={{
                    position: "absolute",
                    left: t * PIXELS_PER_SECOND,
                    bottom: 0,
                  }}
                >
                  <div
                    style={{
                      width: 1,
                      height: 5,
                      background: "rgba(255,255,255,0.06)",
                    }}
                  />
                </div>
              ))}

              {/* Playhead time label */}
              <div
                style={{
                  position: "absolute",
                  left: playheadX + 8,
                  top: 6,
                  pointerEvents: "none",
                  zIndex: 40,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: "monospace",
                    color: "#a78bfa",
                    background: "rgba(124,58,237,0.15)",
                    border: "1px solid rgba(124,58,237,0.3)",
                    padding: "2px 6px",
                    borderRadius: 6,
                  }}
                >
                  {fmtTime(currentTime)}
                </span>
              </div>
            </div>

            {/* ── Track rows ── */}
            <div style={{ position: "relative" }}>
              {/* Grid lines */}
              {majorTicks.map((t) => (
                <div
                  key={`grid${t}`}
                  style={{
                    position: "absolute",
                    left: t * PIXELS_PER_SECOND,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: "rgba(255,255,255,0.04)",
                    zIndex: 0,
                  }}
                />
              ))}

              {/* Voice block tracks */}
              {blocks.length === 0 ? (
                <div
                  style={{
                    height: TRACK_HEIGHT,
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                  }}
                  className="flex items-center justify-center"
                >
                  <span
                    style={{ fontSize: 12, color: "rgba(255,255,255,0.2)" }}
                  >
                    Add blocks to arrange them here
                  </span>
                </div>
              ) : (
                blocks.map((block, i) => {
                  const clr = BLOCK_COLORS[i % BLOCK_COLORS.length];
                  const isSelected = selectedBlockId === block.id;
                  const isDraggingThis = ghostPos?.blockId === block.id;

                  const committedDurSec = getDurSec(block) ?? 5;
                  const displayDurSec =
                    isDraggingThis && ghostPos
                      ? ghostPos.durationSec
                      : committedDurSec;
                  const displayStart =
                    isDraggingThis && ghostPos
                      ? ghostPos.timelineStart
                      : parseFloat(block.timelineStart) || 0;
                  const displayWidth = Math.max(
                    displayDurSec * PIXELS_PER_SECOND,
                    MIN_BLOCK_WIDTH,
                  );
                  const hasRealDuration = getDurSec(block) !== null;

                  return (
                    <div
                      key={block.id}
                      style={{
                        height: TRACK_HEIGHT,
                        position: "relative",
                        borderBottom: "1px solid rgba(255,255,255,0.03)",
                      }}
                    >
                      {/* Drag snap guide */}
                      {isDraggingThis && ghostPos && (
                        <div
                          style={{
                            position: "absolute",
                            left: ghostPos.timelineStart * PIXELS_PER_SECOND,
                            top: 0,
                            bottom: 0,
                            width: 1,
                            zIndex: 5,
                            pointerEvents: "none",
                            background: clr.border,
                          }}
                        />
                      )}

                      {/* Block chip */}
                      <div
                        style={{
                          position: "absolute",
                          left: displayStart * PIXELS_PER_SECOND,
                          top: 10,
                          width: displayWidth,
                          height: TRACK_HEIGHT - 20,
                          zIndex: isDraggingThis ? 20 : 10,
                          cursor: isDraggingThis ? "grabbing" : "grab",
                          transition: isDraggingThis
                            ? "none"
                            : "box-shadow 0.15s ease",
                          willChange: isDraggingThis ? "left, width" : "auto",
                          borderRadius: 10,
                          overflow: "hidden",
                          border: `1px solid ${isSelected ? clr.border : "rgba(255,255,255,0.1)"}`,
                          borderStyle: hasRealDuration ? "solid" : "dashed",
                          boxShadow: isSelected
                            ? `0 4px 20px ${clr.glow}`
                            : isDraggingThis
                              ? "0 8px 32px rgba(0,0,0,0.5)"
                              : "none",
                          opacity: isDraggingThis ? 0.9 : 1,
                        }}
                        onMouseDown={(e) =>
                          handleBlockMouseDown(e, block, "move")
                        }
                        onMouseEnter={() => setHoveredBlock(block.id)}
                        onMouseLeave={() => setHoveredBlock(null)}
                      >
                        {/* Gradient fill */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: `linear-gradient(135deg, ${clr.grad[0]}30, ${clr.grad[1]}18)`,
                          }}
                        />
                        {/* Top accent line */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            background: `linear-gradient(90deg, ${clr.grad[0]}, ${clr.grad[1]})`,
                          }}
                        />

                        {/* Content */}
                        <div
                          style={{
                            position: "relative",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 10px",
                            gap: 8,
                            overflow: "hidden",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "rgba(255,255,255,0.9)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1,
                              }}
                            >
                              {block.voiceDisplayName || `Voice ${i + 1}`}
                            </p>
                            <p
                              style={{
                                fontSize: 9,
                                color: "rgba(255,255,255,0.4)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                marginTop: 2,
                              }}
                            >
                              {block.text?.slice(0, 40) || "…"}
                            </p>
                          </div>
                          <MiniWave
                            count={8}
                            animated={block.status === "processing"}
                            color={clr.grad[0]}
                          />
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background:
                                block.status === "completed"
                                  ? "#34d399"
                                  : block.status === "processing"
                                    ? "#fbbf24"
                                    : "rgba(255,255,255,0.2)",
                            }}
                            className={
                              block.status === "processing"
                                ? "animate-pulse"
                                : ""
                            }
                          />
                        </div>

                        {/* Duration label */}
                        {displayDurSec > 1 && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 3,
                              right: 8,
                              fontSize: 9,
                              fontFamily: "monospace",
                              color: "rgba(255,255,255,0.3)",
                              pointerEvents: "none",
                            }}
                          >
                            {displayDurSec.toFixed(1)}s
                          </div>
                        )}

                        {/* Resize handle */}
                        <div
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 12,
                            cursor: "ew-resize",
                            zIndex: 15,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onMouseDown={(e) =>
                            handleBlockMouseDown(e, block, "resize")
                          }
                        >
                          <div
                            style={{
                              width: 3,
                              height: 20,
                              borderRadius: 9999,
                              background: "rgba(255,255,255,0.2)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(255,255,255,0.5)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(255,255,255,0.2)";
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {/* Music track */}
              {(() => {
                const committedMusicDurSec =
                  getMusicDisplayDuration(backgroundMusic);
                const musicDurSec =
                  musicGhostDur !== null ? musicGhostDur : committedMusicDurSec;
                const musicWidth =
                  musicDurSec !== null
                    ? Math.max(musicDurSec * PIXELS_PER_SECOND, 40)
                    : totalWidth;
                const isMusicResizing = musicGhostDur !== null;

                return (
                  <div
                    style={{
                      height: TRACK_HEIGHT,
                      position: "relative",
                      borderTop: "1px solid rgba(255,255,255,0.04)",
                    }}
                  >
                    {backgroundMusic ? (
                      <div
                        onClick={
                          !isMusicResizing && onTrimClick
                            ? onTrimClick
                            : undefined
                        }
                        style={{
                          position: "absolute",
                          left: 2,
                          top: 10,
                          width: musicWidth,
                          bottom: 10,
                          cursor: isMusicResizing
                            ? "ew-resize"
                            : onTrimClick
                              ? "pointer"
                              : "default",
                          borderRadius: 10,
                          overflow: "hidden",
                          background:
                            "linear-gradient(135deg, rgba(217,119,6,0.25), rgba(180,83,9,0.15))",
                          border: "1px solid rgba(251,191,36,0.3)",
                          boxShadow: "0 2px 12px rgba(217,119,6,0.15)",
                        }}
                      >
                        {/* Top accent */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            background:
                              "linear-gradient(90deg, #d97706, #b45309)",
                          }}
                        />

                        {/* Content */}
                        <div
                          style={{
                            position: "relative",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 10px 0 12px",
                            gap: 8,
                            overflow: "hidden",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "rgba(251,191,36,0.9)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {backgroundMusic.title}
                            </p>
                            <p
                              style={{
                                fontSize: 9,
                                color: "rgba(217,119,6,0.6)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                marginTop: 1,
                              }}
                            >
                              {backgroundMusic.artist}
                            </p>
                          </div>
                          <MiniWave count={8} color="#d97706" />
                          {musicDurSec !== null && (
                            <span
                              style={{
                                fontSize: 9,
                                color: "rgba(251,191,36,0.5)",
                                fontFamily: "monospace",
                                flexShrink: 0,
                              }}
                            >
                              {`${Math.floor(musicDurSec / 60)}:${String(Math.round(musicDurSec) % 60).padStart(2, "0")}`}
                            </span>
                          )}
                        </div>

                        {/* Resize handle */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 12,
                            cursor: "ew-resize",
                            zIndex: 15,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const startDurSec = musicDurSec ?? 30;
                            dragStateRef.current = {
                              blockId: "__music__",
                              mode: "music-resize",
                              startClientX: e.clientX,
                              startScrollLeft:
                                scrollRef.current?.scrollLeft ?? 0,
                              startTimelineStart: 0,
                              startDurationSec: startDurSec,
                            };
                            setMusicGhostDur(startDurSec);
                          }}
                        >
                          <div
                            style={{
                              width: 3,
                              height: 20,
                              borderRadius: 9999,
                              background: "rgba(251,191,36,0.3)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(251,191,36,0.6)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(251,191,36,0.3)";
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          position: "absolute",
                          left: 8,
                          right: 8,
                          top: 10,
                          bottom: 10,
                          borderRadius: 10,
                          border: "1px dashed rgba(217,119,6,0.15)",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 12px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            color: "rgba(217,119,6,0.3)",
                            fontStyle: "italic",
                          }}
                        >
                          No music — click Music in toolbar
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* ── Playhead ── */}
            <div
              style={{
                position: "absolute",
                left: playheadX,
                top: 0,
                bottom: 0,
                pointerEvents: "none",
                zIndex: 25,
              }}
            >
              {/* Diamond head */}
              <div
                style={{
                  position: "absolute",
                  top: RULER_HEIGHT - 10,
                  left: -7,
                  width: 14,
                  height: 14,
                  transform: "rotate(45deg)",
                  background: "#a78bfa",
                  boxShadow: "0 0 10px rgba(167,139,250,0.8)",
                }}
              />
              {/* Line */}
              <div
                style={{
                  position: "absolute",
                  top: RULER_HEIGHT,
                  left: -0.5,
                  bottom: 0,
                  width: 1,
                  background:
                    "linear-gradient(180deg, #a78bfa, rgba(167,139,250,0.2))",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Info bar ── */}
      <div
        className="flex items-center gap-4 px-4 py-1.5"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.05)",
          background: "rgba(0,0,0,0.3)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.2)",
            fontFamily: "monospace",
          }}
        >
          {blocks.length} block{blocks.length !== 1 ? "s" : ""} ·{" "}
          {totalDuration.toFixed(1)}s
        </span>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.12)" }}>
          Snap {SNAP_INTERVAL}s · {PIXELS_PER_SECOND.toFixed(0)}px/s
        </span>
        {/* Zoom controls */}
        <div className="flex items-center gap-1 ml-1">
          <button
            onClick={() =>
              setZoomLevel((z) =>
                Math.max(0.25, parseFloat((z / 1.5).toFixed(4))),
              )
            }
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.35)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 5,
              width: 20,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              lineHeight: 1,
            }}
            title="Zoom out"
          >
            −
          </button>
          <button
            onClick={() => setZoomLevel(1)}
            style={{
              fontSize: 9,
              color: "rgba(255,255,255,0.25)",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 5,
              padding: "0 5px",
              height: 18,
              cursor: "pointer",
              fontFamily: "monospace",
              whiteSpace: "nowrap",
            }}
            title="Reset zoom"
          >
            {Math.round(zoomLevel * 100)}%
          </button>
          <button
            onClick={() =>
              setZoomLevel((z) => Math.min(8, parseFloat((z * 1.5).toFixed(4))))
            }
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.35)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 5,
              width: 20,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              lineHeight: 1,
            }}
            title="Zoom in"
          >
            +
          </button>
        </div>
        {(ghostPos || musicGhostDur !== null) && (
          <span
            style={{
              fontSize: 10,
              fontFamily: "monospace",
              marginLeft: "auto",
              color: musicGhostDur !== null ? "#fbbf24" : "#a78bfa",
            }}
          >
            {musicGhostDur !== null
              ? `♪ ${musicGhostDur.toFixed(2)}s`
              : dragStateRef.current?.mode === "resize"
                ? `⟺ ${ghostPos.durationSec.toFixed(2)}s`
                : `→ ${ghostPos.timelineStart.toFixed(2)}s`}
          </span>
        )}
      </div>
    </div>
  );
};

export default Timeline;
